import { formatCost } from "@/lib/money";
import { ViewUserProfileLink } from "@/components/identity-link";
import { Page } from "@/components/page-layout";
import { Badge } from "@/components/ui/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { Dimension } from "@gram/client/models/components/queryfilter.js";
import { type QueryRow } from "@gram/client/models/components/queryrow.js";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ChevronLeft, Download, Home, Info, RotateCcw } from "lucide-react";
import { CostMeasureLabel } from "@/components/estimated-cost";
import {
  SeriesFocusContext,
  useSeriesFocusState,
} from "@/components/chart/seriesFocus";
import { BreakdownBar } from "./BreakdownBar";
import { breakdownCaption, breakdownTitle } from "./breakdownCopy";
import { CostTable } from "./CostTable";
import { downloadCsv, slugify, toCsv } from "./csv";
import {
  type Crumb,
  displayName,
  entityBadgeVariant,
  formatWorkUnits,
  friendlyName,
  isAttributionDim,
  LABELS,
  llmTokens,
  type Measures,
  pluralLabel,
} from "./taxonomy";

// ── Formatting helpers ──────────────────────────────────────────────────────

// ── CSV export ──────────────────────────────────────────────────────────────

// Serialize the current table rows to CSV — same columns the table shows
// (minus the Trend sparkline), with raw numbers so the file is spreadsheet-ready.
// Attribution breakdowns swap "Tool Calls" for "Tokens Added" to mirror the table.
function buildCostCsv(
  rows: QueryRow[],
  groupLabel: string,
  groupBy: Dimension,
): string {
  const total = rows.reduce((sum, r) => sum + (r.measures.totalCost ?? 0), 0);
  const cacheMetric = isAttributionDim(groupBy);
  const header = [
    groupLabel,
    "Total Cost",
    "% Share",
    "Cost / Session",
    "Sessions",
    cacheMetric ? "Tokens Added" : "Tool Calls",
    "Tokens",
  ];
  const body = rows.map((r) => {
    const cost = r.measures.totalCost ?? 0;
    const chats = r.measures.totalChats ?? 0;
    return [
      displayName(groupBy, r.groupValue),
      cost.toFixed(2),
      total > 0 ? ((cost / total) * 100).toFixed(1) : "0.0",
      chats > 0 ? (cost / chats).toFixed(2) : "0.00",
      chats,
      cacheMetric
        ? (r.measures.cacheCreationInputTokens ?? 0)
        : (r.measures.totalToolCalls ?? 0),
      llmTokens(r.measures),
    ];
  });
  return toCsv(header, body);
}

// The efficiency lens's CSV — mirrors that table's columns. Per-unit cells are
// empty (not 0) where a row has no scored work, matching the table's "—".
function buildEfficiencyCsv(
  rows: QueryRow[],
  groupLabel: string,
  groupBy: Dimension,
): string {
  const totalUnits = rows.reduce(
    (sum, r) => sum + (r.measures.totalWorkUnits ?? 0),
    0,
  );
  const header = [
    groupLabel,
    "Work Delivered",
    "% Share",
    "Cost Efficiency",
    "Sessions",
    "Token Efficiency",
    "Total Cost",
  ];
  const body = rows.map((r) => {
    const units = r.measures.totalWorkUnits ?? 0;
    const scored = units > 0;
    return [
      displayName(groupBy, r.groupValue),
      units.toFixed(1),
      totalUnits > 0 && scored
        ? ((units / totalUnits) * 100).toFixed(1)
        : "0.0",
      scored ? ((r.measures.scoredCost ?? 0) / units).toFixed(2) : "",
      r.measures.totalChats ?? 0,
      scored ? Math.round((r.measures.scoredTokens ?? 0) / units) : "",
      (r.measures.totalCost ?? 0).toFixed(2),
    ];
  });
  return toCsv(header, body);
}

// The search placeholder's noun: the axis plural sentence-cased — words
// lowercase except acronyms ("Users" → "users", "MCP Servers" → "MCP servers").
function searchNoun(label: string): string {
  return label
    .split(" ")
    .map((word) => (word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(" ");
}

// ── Small presentational pieces ─────────────────────────────────────────────

// The page's bordered ghost buttons share one core look; the control-bar
// Reset, the table Export CSV, and the nav buttons (Home, Back) compose their
// size/spacing on top of it.
const GHOST_BUTTON_CLASS =
  "text-muted-foreground hover:text-foreground border-border hover:bg-muted inline-flex items-center border bg-transparent text-sm transition-colors";
const BAR_BUTTON_CLASS = cn(
  GHOST_BUTTON_CLASS,
  "h-10 shrink-0 gap-1.5 px-3 font-medium disabled:pointer-events-none disabled:opacity-40",
);
const NAV_BUTTON_CLASS = cn(GHOST_BUTTON_CLASS, "gap-1 py-1.5 pr-3 pl-2.5");

// A headline metric in the profile header (Cost / Sessions / …), echoing the
// big Followers/Following/Likes numbers in the reference design.
function HeaderStat({
  label,
  value,
  onClick,
}: {
  label: ReactNode;
  value: string;
  // When set, the stat becomes a button — used to turn "Agent sessions" into the
  // header entry point for the per-session list.
  onClick?: () => void;
}): JSX.Element {
  const inner = (
    <>
      <span className="text-eyebrow">{label}</span>
      <span className="font-display text-3xl font-thin tabular-nums">
        {value}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="hover:bg-muted -mx-2 -my-1 flex flex-col gap-1 px-2 py-1 text-left transition-colors"
      >
        {inner}
      </button>
    );
  }
  return <div className="flex flex-col gap-1">{inner}</div>;
}

// Walk up from `el` to the nearest ancestor that actually scrolls vertically.
// The app shell scrolls an inner container (Page.Body / TabsContent), not the
// window — callers that need the scrollport (sticky pinning, scroll-to-top)
// must find that ancestor rather than assuming `window`.
function findVerticalScrollParent(el: HTMLElement | null): HTMLElement | null {
  let root: HTMLElement | null = el?.parentElement ?? null;
  while (root && !/auto|scroll/.test(getComputedStyle(root).overflowY)) {
    root = root.parentElement;
  }
  return root;
}

// How far above the breakdown section a drill lands, so the section's heading
// isn't flush against the top of the scrollport. Doubles as the tolerance for
// "is the reader looking at the breakdown?" — see the scroll effects below.
const BREAKDOWN_LEAD_IN = 12;

// ── EntityProfile ───────────────────────────────────────────────────────────

export type EntityProfileProps = {
  // The entity this profile represents; null = the org root (bird's-eye).
  entity: Crumb | null;
  // At the root, an attribution breakdown presents as a collection (e.g. "MCP
  // Servers") instead of the project — supplies the hero title + icon. Null
  // otherwise (project root or a drilled entity).
  collection: { dim: Dimension; label: string } | null;
  // Whether this is an attribution lens: swaps the "Tool calls" hero stat for
  // "Tokens added" (cache-creation tokens), the meaningful measure for these cuts.
  cacheMetric: boolean;
  // The efficiency lens: the hero and table read the work-units measures (work
  // units delivered, cost per unit) instead of the activity measures.
  efficiency: boolean;
  // Navigate up one ancestor. No-op at the root.
  onBack: () => void;
  // Jump straight back to the org root.
  onHome: () => void;
  // The current project's name — the dashboard is project-scoped, so the root
  // node represents this project rather than the whole organization.
  projectName: string;
  // The immediate parent's value, for the "Back to …" control.
  parentValue: string | null;
  // The full drill path (root → the entity in view), which the breakdown
  // caption names so it describes the slice actually on screen.
  path: Crumb[];
  // Headline measures summed over this entity's children.
  stats: Measures;
  // The dimension the child table is grouped by (drives labels + CostTable).
  groupBy: Dimension;
  // Whether rows can be drilled — false when no populated level exists below.
  canDrill: boolean;
  // The current breakdown axis value (a Dimension or the sessions sentinel) and
  // its selectable options, plus the change handler.
  axisValue: string;
  axisOptions: { value: string; label: string }[];
  // Optional caveat for the current breakdown axis, shown as an info tooltip
  // beside the select (e.g. the root Skill cut excludes subagent-run skills).
  axisHint?: string;
  onAxisChange: (value: string) => void;
  // Free-text filter over the visible table rows (dimension rows or sessions),
  // owned by the explorer so it can filter both row sources consistently.
  searchValue: string;
  onSearchChange: (value: string) => void;
  // The child rows + drill handler.
  rows: QueryRow[];
  // The view's resolved billing mode; "metered" shows real cost instead of the
  // API-rate estimate on the cost columns.
  billingMode?: string;
  onDrill: (row: QueryRow) => void;
  // When set, replaces the dimension CostTable (the per-session list in sessions
  // mode). The override owns its own loading/empty/error states.
  tableOverride?: ReactNode;
  // CSV export for a `tableOverride`'s rows. Supplied alongside the override so
  // the export control above the table keeps working on the sessions breakdown
  // instead of unmounting and leaving a gap.
  overrideCsv?: { rowCount: number; build: () => string };
  // Switch the breakdown to the per-session list — wired to the clickable
  // "Agent sessions" header stat. Omitted when already in sessions mode.
  onViewSessions?: () => void;
  // Reset the whole view to its defaults (drill path, axis, dataset, range,
  // search) — the control bar's Reset button.
  onReset: () => void;
  // Per-group daily cost series for the row sparklines.
  seriesByGroup: Map<string, number[]>;
  // The active dataset (spend slice) and its options, rendered in the top
  // control bar beside the date picker. `all` is the full project spend; the
  // others narrow to a Claude attribution lens (MCP / Subagents / Skills).
  datasetValue: string;
  datasetOptions: { value: string; label: string }[];
  onDatasetChange: (value: string) => void;
  // The date-range picker control, rendered in the top control bar.
  rangePicker: ReactNode;
  // Human date-range label (e.g. "June 15–19") for the CSV export filename.
  rangeLabel: string;
  // The summary widgets row (trend chart, mix, KPIs), rendered above the table.
  widgets: ReactNode;
  // The stacked cost-over-time chart, rendered inside the breakdown section
  // between the axis/search controls and the table — it stacks by the same
  // axis the section's control bar selects, so it reads as part of the
  // breakdown.
  chart?: ReactNode;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Generalized profile page for one node of the cost taxonomy. The same layout
 * renders the org root, a Division, a Department, a User, an Agent — driven entirely
 * by props: a bold header (avatar + name + headline stats), the entity's own
 * attribute grid, and the grouped table of its children.
 */
export function EntityProfile({
  entity,
  collection,
  cacheMetric,
  efficiency,
  onBack,
  onHome,
  projectName,
  parentValue,
  path,
  stats,
  groupBy,
  canDrill,
  axisValue,
  axisOptions,
  axisHint,
  onAxisChange,
  searchValue,
  onSearchChange,
  rows,
  billingMode,
  onDrill,
  tableOverride,
  overrideCsv,
  onViewSessions,
  onReset,
  seriesByGroup,
  datasetValue,
  datasetOptions,
  onDatasetChange,
  rangePicker,
  rangeLabel,
  widgets,
  chart,
  isLoading,
  isError,
}: EntityProfileProps): JSX.Element {
  const groupLabel = LABELS[groupBy] ?? "Group";

  // The hover/color link between the breakdown chart and the breakdown table.
  // They are siblings, so this has to be owned here and provided to both.
  const seriesFocus = useSeriesFocusState();

  const title = entity
    ? friendlyName(entity.dim, entity.value)
    : (collection?.label ?? projectName ?? "All costs");
  const typeLabel = entity
    ? (LABELS[entity.dim] ?? "Group")
    : collection
      ? "Breakdown"
      : "Project";
  // `title` title-cases a user's address into a name ("Olivia Novak"), which is
  // friendlier but ambiguous between two people — keep the address it came from
  // alongside it. Only users have one; every other value is already its label.
  // The user dimension can also hold a device hostname (the fallback for
  // sessions with no email) — no address to repeat there.
  const emailSuffix =
    entity?.dim === Dimension.Email && entity.value.includes("@")
      ? entity.value
      : null;
  const badgeVariant = entityBadgeVariant(
    entity?.dim ?? collection?.dim ?? null,
  );

  // The control bar pins to the top of the scrollport once scrolled past. A
  // 1px sentinel above the sticky wrapper drives the pinned styling (full-
  // width blur band + hairline): the wrapper is stuck exactly while the
  // sentinel is scrolled out of the container. Observed against the actual
  // scroll ancestor — the app shell scrolls an inner container, so the
  // viewport default would fire ~a header-height too late.
  const [pinned, setPinned] = useState(false);
  const pinSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = pinSentinelRef.current;
    if (!sentinel) return;
    const root = findVerticalScrollParent(sentinel);
    const observer = new IntersectionObserver(
      ([entry]) => setPinned(entry ? !entry.isIntersecting : false),
      { root, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Drill navigation keeps the EntityProfile mounted and only swaps props, so
  // the browser never resets scroll on its own, and a mid-table click would
  // otherwise land the new profile still scrolled down — looking like nothing
  // moved. Reset scroll on every drill, but not always to the very top: a
  // drill launched from the breakdown (a table row or a bar segment) keeps
  // the breakdown in view, so the chart and table you just clicked stay where
  // your eyes already are. Only a drill from higher up the page — the hero,
  // the widgets, a breadcrumb — goes back to the top.
  // useLayoutEffect so the move lands before paint (no flash of mid-page).
  const pathKey = path.map((c) => `${c.dim}:${c.value}`).join("/");
  const breakdownRef = useRef<HTMLDivElement>(null);

  // Where the breakdown section starts, in the scrollport's coordinates.
  const breakdownTop = (root: HTMLElement): number => {
    const section = breakdownRef.current;
    if (!section) return 0;
    return (
      section.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      root.scrollTop
    );
  };

  // Whether the reader was looking at the breakdown when they clicked. It has
  // to be answered from the scroll position BEFORE the drill re-rendered the
  // page — by then the old position is gone and the section has moved — so a
  // scroll listener keeps the answer current and the drill effect only reads
  // it.
  const wasInBreakdownRef = useRef(false);
  useLayoutEffect(() => {
    const root = findVerticalScrollParent(pinSentinelRef.current);
    if (!root) return;
    const record = (): void => {
      // The same lead-in the drill scrolls to, as tolerance: landing 12px
      // above the section still counts as being in it, so a second drill in a
      // row doesn't read as "they scrolled away" and jump to the top.
      wasInBreakdownRef.current =
        root.scrollTop >= breakdownTop(root) - BREAKDOWN_LEAD_IN;
    };
    record();
    root.addEventListener("scroll", record, { passive: true });
    return () => root.removeEventListener("scroll", record);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- breakdownTop only reads refs
  }, []);

  useLayoutEffect(() => {
    const root = findVerticalScrollParent(pinSentinelRef.current);
    if (!root) {
      window.scrollTo(0, 0);
      return;
    }
    root.scrollTop = wasInBreakdownRef.current
      ? Math.max(breakdownTop(root) - BREAKDOWN_LEAD_IN, 0)
      : 0;
    wasInBreakdownRef.current =
      root.scrollTop >= breakdownTop(root) - BREAKDOWN_LEAD_IN;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- breakdownTop only reads refs
  }, [pathKey]);

  // The efficiency lens quotes the slice's work units where the cost lenses
  // quote spend — the caption's grammar fits either ("… — 1,204.5 work units
  // across 4 Models.").
  const caption = breakdownCaption({
    axisValue,
    groupBy,
    path,
    costLabel: efficiency
      ? `${formatWorkUnits(stats.workUnits)} work delivered`
      : formatCost(stats.cost),
    groupCount: isError ? 0 : rows.length,
  });

  // The hero's third and fourth stats, by lens (the first two — cost and
  // sessions — are universal).
  function heroTrailingStats(): JSX.Element {
    if (efficiency) {
      const unitCost =
        stats.workUnits > 0 ? stats.scoredCost / stats.workUnits : null;
      return (
        <>
          <HeaderStat
            label="Work delivered"
            value={formatWorkUnits(stats.workUnits)}
          />
          <HeaderStat
            label="Cost efficiency"
            value={unitCost !== null ? formatCost(unitCost) : "—"}
          />
        </>
      );
    }
    if (cacheMetric) {
      return (
        <>
          <HeaderStat
            label="Tokens added"
            value={stats.cacheCreation.toLocaleString()}
          />
          <HeaderStat label="Tokens" value={stats.tokens.toLocaleString()} />
        </>
      );
    }
    return (
      <>
        <HeaderStat label="Tool calls" value={stats.tools.toLocaleString()} />
        <HeaderStat label="Tokens" value={stats.tokens.toLocaleString()} />
      </>
    );
  }

  // The "Back to …" label names the immediate parent with its own dimension's
  // labeling (the parent crumb is second-to-last on the path; the last crumb is
  // the entity in view), falling back to the project at the root.
  const parentDim = path[path.length - 2]?.dim;
  const backLabel =
    parentValue !== null && parentDim !== undefined
      ? displayName(parentDim, parentValue)
      : projectName || "All costs";

  // Whichever table is on screen owns the export: the dimension rows by default,
  // the override's rows (sessions) when it has supplied a builder. The control
  // sits above the table itself (not in the page-scope toolbar) so it reads as
  // exporting those rows, and only disables when the table is empty.
  const csvExport = overrideCsv
    ? {
        rowCount: overrideCsv.rowCount,
        run: () =>
          downloadCsv(
            `${slugify(title)}-sessions-${slugify(rangeLabel)}.csv`,
            overrideCsv.build(),
          ),
      }
    : {
        rowCount: rows.length,
        run: () =>
          downloadCsv(
            `${slugify(title)}-by-${slugify(groupLabel)}-${slugify(rangeLabel)}.csv`,
            efficiency
              ? buildEfficiencyCsv(rows, groupLabel, groupBy)
              : buildCostCsv(rows, groupLabel, groupBy),
          ),
      };

  const exportCsvButton = (
    <button
      type="button"
      onClick={csvExport.run}
      disabled={csvExport.rowCount === 0}
      className={BAR_BUTTON_CLASS}
    >
      <Download className="size-3.5 shrink-0" />
      Export CSV
    </button>
  );

  // Placeholder names what the search box narrows: the sessions list when the
  // override table is on screen, otherwise the current axis's plural.
  const searchPlaceholder = tableOverride
    ? "Search sessions..."
    : `Search ${searchNoun(pluralLabel(groupBy))}...`;
  const searchActive = searchValue.trim().length > 0;

  // The default dimension table; replaced by `tableOverride` (the session list)
  // when one is supplied.
  const dimensionTable = isError ? (
    <Text className="text-muted-foreground">Failed to load cost data.</Text>
  ) : (
    <CostTable
      // Remount on a lens switch so the default sort (work units vs cost)
      // re-applies instead of carrying the other lens's sort state over.
      key={efficiency ? "efficiency" : "cost"}
      rows={rows}
      groupLabel={groupLabel}
      groupBy={groupBy}
      canDrill={canDrill}
      onDrill={onDrill}
      seriesByGroup={seriesByGroup}
      isLoading={isLoading}
      billingMode={billingMode}
      efficiency={efficiency}
      emptyMessage={searchActive ? "No matches for your search." : undefined}
    />
  );

  // The dataset selector: a grey "Dataset" label box wrapping the select,
  // rendered in the top control bar's leading (page-scope) group.
  const datasetControl = (
    <div className="border-border bg-muted flex h-10 items-stretch overflow-hidden border text-sm">
      <span className="text-muted-foreground flex items-center pr-2 pl-3 font-medium">
        Dataset
      </span>
      <Select value={datasetValue} onValueChange={onDatasetChange}>
        <SelectTrigger className="border-border bg-background hover:bg-muted data-[state=open]:bg-muted !h-full w-auto cursor-pointer gap-1.5 border-0 border-l py-1 pr-2.5 pl-3 font-medium shadow-none transition-colors">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {datasetOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="flex w-full flex-col">
      {/* Top strip: the back controls, shown when drilled into an entity. */}
      <div className="mx-auto w-full max-w-7xl px-8 pt-5">
        {/* Cost Home (jump to root) + Back (one level up). Always mounted so
            they animate in/out across drills — conditional rendering would
            pop. The EntityProfile instance persists across drills, so the
            class swap triggers a real transition. */}
        <div
          aria-hidden={!entity}
          className={cn(
            "flex items-center gap-2 overflow-hidden transition-all duration-200 ease-out",
            entity
              ? "mb-3 max-h-10 translate-x-0 opacity-100"
              : "pointer-events-none max-h-0 -translate-x-1 opacity-0",
          )}
        >
          {/* Only useful below depth 1 — at the root's immediate child,
              "Back to All costs" already jumps home. */}
          {parentValue !== null && (
            <button
              type="button"
              onClick={onHome}
              tabIndex={entity ? 0 : -1}
              className={NAV_BUTTON_CLASS}
            >
              <Home className="size-3.5 shrink-0" />
              <span>Cost Overview</span>
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            tabIndex={entity ? 0 : -1}
            className={NAV_BUTTON_CLASS}
          >
            <ChevronLeft className="size-3.5 shrink-0" />
            <span className="max-w-[220px] truncate">
              Back to{" "}
              <span className="text-foreground font-semibold">{backLabel}</span>
            </span>
          </button>
        </div>
      </div>
      {/* Flat hero: name + headline stats on the page surface, closed by a
          hairline rule. */}
      <div className="border-border w-full border-b">
        <div className="mx-auto w-full max-w-7xl px-8 pt-8 pb-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="min-w-0">
                {/* The name leads; the type chip trails it, colour-coded by
                    entity family (see entityBadgeVariant). `min-w-0` on the
                    heading keeps the truncation on the name, so the chip stays
                    legible however long the value is. */}
                <Page.Eyebrow className="mb-2" />
                <div className="flex items-center gap-3">
                  <h1 className="text-display-sm min-w-0 truncate font-thin">
                    {title}
                    {emailSuffix && (
                      <span className="text-muted-foreground ml-2 text-xl font-normal">
                        ({emailSuffix})
                      </span>
                    )}
                  </h1>
                  <Badge
                    size="md"
                    variant={badgeVariant}
                    background
                    className="shrink-0"
                  >
                    <Badge.Text>{typeLabel}</Badge.Text>
                  </Badge>
                  {/* Spend is one subsystem's view of a person; the profile is
                      where it sits beside their access, risk and devices. */}
                  {emailSuffix && (
                    <ViewUserProfileLink identifier={{ email: emailSuffix }} />
                  )}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 gap-8">
              <HeaderStat
                label={<CostMeasureLabel billingMode={billingMode} />}
                value={formatCost(stats.cost)}
              />
              <HeaderStat
                label="Agent sessions"
                value={stats.sessions.toLocaleString()}
                onClick={onViewSessions}
              />
              {heroTrailingStats()}
            </div>
          </div>
        </div>
      </div>

      {/* Page-scope controls sit under the headline numbers: dataset + range
          shape every number on the page, and Reset acts on the current view.
          Once scrolled past, the bar pins to the top of the scrollport (the
          sentinel above drives the pinned styling: a full-width blur band
          with a hairline). The breakdown axis track, row search, and CSV
          export live with the chart/table below — not here — so they stay
          next to the content they reshape and the rows they export. */}
      <div ref={pinSentinelRef} aria-hidden="true" className="h-px w-full" />
      <div
        className={cn(
          "sticky top-0 z-20 w-full",
          pinned && "border-border bg-background/85 border-b backdrop-blur-md",
        )}
      >
        <div className="mx-auto w-full max-w-7xl px-8 py-2">
          <Page.Toolbar>
            <Page.Toolbar.Leading>
              {datasetControl}
              {rangePicker}
            </Page.Toolbar.Leading>
            <Page.Toolbar.Actions>
              <button
                type="button"
                onClick={onReset}
                className={BAR_BUTTON_CLASS}
              >
                <RotateCcw className="size-3.5 shrink-0" />
                Reset
              </button>
            </Page.Toolbar.Actions>
          </Page.Toolbar>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-8 pt-4 pb-24">
        {widgets}
        {/* The breakdown is its own section under the summary widgets, so it
            opens on a rule rather than floating off the last widget. The
            heading states the current cut ("Cost by Model") — echoing the lit
            segment in the control bar below — with the caption saying what
            the cut is doing in the user's own numbers. Axis track + search
            sit here, immediately above the chart/table they affect. */}
        <div
          ref={breakdownRef}
          className="border-border flex flex-col gap-3 border-t pt-6"
        >
          <div className="flex flex-col gap-0.5">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              {breakdownTitle(axisValue, groupBy, efficiency)}
              {/* No general "what is a breakdown" note — defining it in the
                  abstract read as jargon, and the caption below says it
                  against the slice actually on screen. The icon is left for
                  axes that carry a real caveat, so its presence means
                  something. */}
              {axisHint && (
                <Tooltip>
                  <TooltipTrigger
                    aria-label={axisHint}
                    className="text-muted-foreground inline-flex cursor-help"
                  >
                    <Info className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-64">
                    {axisHint}
                  </TooltipContent>
                </Tooltip>
              )}
            </h2>
            <p className="text-muted-foreground text-xs">{caption}</p>
          </div>
          <BreakdownBar
            axisValue={axisValue}
            axisOptions={axisOptions}
            onAxisChange={onAxisChange}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
          />
          {/* Chart and table are two views of the same series, so they share
              hover focus and the chart's colors: the table dots each row in
              its stack's color and fades the rest while a bar is hovered. */}
          <SeriesFocusContext.Provider value={seriesFocus}>
            {chart}
            {/* Export sits immediately above the table so it reads as exporting
                these rows — not the whole page's spend. */}
            <div className="flex items-center justify-end">
              {exportCsvButton}
            </div>
            {tableOverride ?? dimensionTable}
          </SeriesFocusContext.Provider>
        </div>
      </div>
    </div>
  );
}
